import * as React from "react";
import { ColorPaletteProp } from "@mui/joy/styles";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Chip from "@mui/joy/Chip";
import Divider from "@mui/joy/Divider";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Table from "@mui/joy/Table";
import Sheet from "@mui/joy/Sheet";
import Checkbox from "@mui/joy/Checkbox";
import IconButton, { iconButtonClasses } from "@mui/joy/IconButton";
import Typography from "@mui/joy/Typography";
import Menu from "@mui/joy/Menu";
import MenuButton from "@mui/joy/MenuButton";
import MenuItem from "@mui/joy/MenuItem";
import Dropdown from "@mui/joy/Dropdown";

import FilterAltIcon from "@mui/icons-material/FilterAlt";
import SearchIcon from "@mui/icons-material/Search";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import BlockIcon from "@mui/icons-material/Block";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";

import { listCandidates, getVerificationImage } from "../api/methods";
import { Candidate } from "../api/interfaces";
import AlertDialogModal from "./ConfirmationModal";
import { RowMenuProps } from "../api/interfaces";
import { formatDate } from "../utils";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

type Order = "asc" | "desc";

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(
  array: readonly T[],
  comparator: (a: T, b: T) => number
) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

export default function StatusTable() {
  const [candidate, setCandidate] = React.useState<Order>("desc");
  const [selected, setSelected] = React.useState<readonly string[]>([]);
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("");
  const [equipmentCodeFilter, setEquipmentCodeFilter] = React.useState("");
  const [agentFilter, setAgentFilter] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(0);
  const [rows, setRows] = React.useState<Candidate[]>([]);
  const [selectedItemId, setSelectedItemId] = React.useState<string | null>(
    null
  );

  const handleOpen = (itemId: string) => {
    setSelectedItemId(itemId);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const RowMenu: React.FC<RowMenuProps> = ({ id }) => {
    return (
      <Dropdown>
        <MenuButton
          slots={{ root: IconButton }}
          slotProps={{
            root: { variant: "plain", color: "neutral", size: "sm" },
          }}
        >
          <MoreHorizRoundedIcon />
        </MenuButton>
        <Menu size="sm" sx={{ minWidth: 140 }}>
          <MenuItem>Voir</MenuItem>
          <Divider />
          <MenuItem color="danger" onClick={() => handleOpen(id)}>
            Supprimer
          </MenuItem>
        </Menu>
      </Dropdown>
    );
  };

  const handleDeleteSuccess = () => {
    if (selectedItemId) {
      setRows((prevRows) =>
        prevRows.filter((row) => row._id !== selectedItemId)
      );
      setOpen(false);
    }
  };

  React.useEffect(() => {
    let isMounted = true;
    const fetchCandidatesStatus = async () => {
      try {
        const response = await listCandidates();
        console.log("herrreee");
        setRows(response.data);
        console.log(response.data);
      } catch (error) {
        console.log("Failed to fetch work orders");
      } finally {
        console.log("OK");
      }
    };

    fetchCandidatesStatus();

    return () => {
      // Clean up on unmount
      isMounted = false;
    };
  }, []);

  const handleViewImage = async (imageName: string) => {
    try {
      const response = await getVerificationImage(imageName);
      const imageUrl = URL.createObjectURL(response.data);
      window.open(imageUrl, "_blank");
    } catch (error) {
      console.error("Error fetching verification image:", error);
    }
  };

  const rowsPerPage = 5;
  const uniqueStatuses = Array.from(new Set(rows.map((row) => row?.status)));
  const indexOfLastRow = (currentPage + 1) * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;

  const handleStatusFilterChange = (
    event: React.SyntheticEvent | null,
    newValue: string | null
  ) => {
    console.log(newValue);
    setStatusFilter(newValue !== null ? newValue : "");
  };

  const filteredRows = rows.filter((row) => {
    return (
      (!statusFilter || row.status?.includes(statusFilter)) &&
      (row.firstname.toLowerCase().includes(searchQuery.toLowerCase()) ||
        row.lastname.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  });

  let currentRows = filteredRows.slice(indexOfFirstRow, indexOfLastRow);
  type PageItem = number | "...";

  const getPageNumbers = (): PageItem[] => {
    const totalPages = Math.ceil(filteredRows.length / rowsPerPage);
    const pages: PageItem[] = [];
    const visiblePages = 2;

    // Generate pages around the current page
    for (
      let i = Math.max(1, currentPage - visiblePages);
      i <= Math.min(totalPages, currentPage + 1 + visiblePages);
      i++
    ) {
      pages.push(i);
    }
    if (typeof pages[0] === "number" && pages[0] > 1) {
      pages.unshift(1);
      if (typeof pages[1] === "number" && pages[1] !== 2) {
        pages.splice(1, 0, "...");
      }
    }
    if (
      typeof pages[pages.length - 1] === "number" &&
      pages[pages.length - 1] < totalPages
    ) {
      if (
        typeof pages[pages.length - 1] === "number" &&
        pages[pages.length - 1] !== totalPages - 1
      ) {
        pages.push("...");
      }
      pages.push(totalPages);
    }
    return pages;
  };

  const handleNextPage = () => {
    setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) setCurrentPage((prevPage) => prevPage - 1);
  };

  const renderFilters = () => (
    <React.Fragment>
      <FormControl size="sm">
        <FormLabel>Status</FormLabel>
        <Select
          size="sm"
          placeholder="Filter by status"
          value={statusFilter}
          onChange={handleStatusFilterChange}
          slotProps={{ button: { sx: { whiteSpace: "nowrap" } } }}
        >
          <Option value="">All</Option>
          {uniqueStatuses.map((status) => (
            <Option key={status} value={status}>
              {status}
            </Option>
          ))}
        </Select>
      </FormControl>
    </React.Fragment>
  );
  return (
    <React.Fragment>
      <Sheet
        className="SearchAndFilters-mobile"
        sx={{
          display: { xs: "flex", sm: "none" },
          my: 1,
          gap: 1,
        }}
      >
        <Input
          size="sm"
          placeholder="Search"
          startDecorator={<SearchIcon />}
          sx={{ flexGrow: 1 }}
          // onChange={handleInputChange}
        />
        <IconButton
          size="sm"
          variant="outlined"
          color="neutral"
          onClick={() => setOpen(true)}
        >
          <FilterAltIcon />
        </IconButton>
      </Sheet>
      <Box
        className="SearchAndFilters-tabletUp"
        sx={{
          borderRadius: "sm",
          py: 2,
          display: { xs: "none", sm: "flex" },
          flexWrap: "wrap",
          gap: 1.5,
          "& > *": {
            minWidth: { xs: "120px", md: "160px" },
          },
        }}
      >
        <FormControl sx={{ flex: 1 }} size="sm">
          <FormLabel>Recherche</FormLabel>
          <Input
            size="sm"
            placeholder="Search"
            startDecorator={<SearchIcon />}
            onChange={(event: any) => {
              setSearchQuery(event.target.value);
            }}
          />
        </FormControl>
        {renderFilters()}
      </Box>
      <Sheet
        className="OrderTableContainer"
        variant="outlined"
        sx={{
          display: { xs: "none", sm: "initial" },
          width: "100%",
          borderRadius: "sm",
          flexShrink: 1,
          overflow: "auto",
          minHeight: 0,
        }}
      >
        <Table
          aria-labelledby="tableTitle"
          stickyHeader
          hoverRow
          sx={{
            "--TableCell-headBackground":
              "var(--joy-palette-background-level1)",
            "--Table-headerUnderlineThickness": "1px",
            "--TableRow-hoverBackground":
              "var(--joy-palette-background-level1)",
            "--TableCell-paddingY": "4px",
            "--TableCell-paddingX": "8px",
          }}
        >
          <thead>
            <tr>
              <th
                style={{ width: 48, textAlign: "center", padding: "12px 6px" }}
              >
                <Checkbox
                  size="sm"
                  indeterminate={
                    selected.length > 0 && selected.length !== rows.length
                  }
                  checked={selected.length === rows.length}
                  onChange={(event) => {
                    setSelected(
                      event.target.checked ? rows.map((row) => row._id) : []
                    );
                  }}
                  color={
                    selected.length > 0 || selected.length === rows.length
                      ? "primary"
                      : undefined
                  }
                  sx={{ verticalAlign: "text-bottom" }}
                />
              </th>
              <th style={{ width: 80, padding: "12px 6px" }}>ID</th>
              <th style={{ width: 80, padding: "12px 6px" }}>Prénom</th>
              <th style={{ width: 80, padding: "12px 6px" }}>Nom</th>
              <th style={{ width: 80, padding: "12px 6px" }}>Status</th>
              <th style={{ width: 80, padding: "12px 6px" }}>Date de départ</th>
              <th style={{ width: 80, padding: "12px 6px" }}>Fichier</th>
              <th style={{ width: 40, padding: "12px 6px" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.map((row) => (
              <tr key={row._id}>
                <td style={{ textAlign: "center", width: 120 }}>
                  <Checkbox
                    size="sm"
                    checked={selected.includes(row._id)}
                    color={selected.includes(row._id) ? "primary" : undefined}
                    onChange={(event) => {
                      setSelected((ids) =>
                        event.target.checked
                          ? ids.concat(row._id)
                          : ids.filter((itemId) => itemId !== row._id)
                      );
                    }}
                    slotProps={{ checkbox: { sx: { textAlign: "left" } } }}
                    sx={{ verticalAlign: "text-bottom" }}
                  />
                </td>
                <td>
                  <Typography level="body-xs">{row.entry_id}</Typography>
                </td>
                <td>
                  <Typography level="body-xs">{row.firstname}</Typography>
                </td>
                <td>
                  <Typography level="body-xs">{row.lastname}</Typography>
                </td>
                <td>
                  <Chip
                    variant="soft"
                    size="sm"
                    startDecorator={
                      {
                        Success: <CheckRoundedIcon />,
                        Inprogress: <AutorenewRoundedIcon />,
                        Failed: <BlockIcon />,
                      }[row.status]
                    }
                    color={
                      {
                        Success: "success",
                        Inprogress: "neutral",
                        Failed: "danger",
                      }[row.status] as ColorPaletteProp
                    }
                  >
                    {row.status}
                  </Chip>
                </td>

                <td>
                  <Typography level="body-xs">
                    {" "}
                    {formatDate(row.departure_date)}
                  </Typography>
                </td>
                <td>
                  <td>
                    <Button
                      size="sm"
                      disabled={row.verification_image === null}
                      variant="outlined"
                      onClick={() => {
                        if (row.verification_image !== null) {
                          handleViewImage(row.verification_image);
                        }
                      }}
                    >
                      View Image
                    </Button>
                  </td>
                </td>
                <td>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                    {/* <Link level="body-xs" component="button">
                      Download
                    </Link> */}
                    <RowMenu id={row._id} />
                  </Box>
                </td>
              </tr>
            ))}
          </tbody>
          {selectedItemId && (
            <AlertDialogModal
              item="workorder"
              id={selectedItemId}
              open={open}
              onClose={handleClose}
              onDeleteSuccess={handleDeleteSuccess}
            />
          )}
        </Table>
      </Sheet>
      <Box
        className="Pagination-laptopUp"
        sx={{
          pt: 2,
          gap: 1,
          [`& .${iconButtonClasses.root}`]: { borderRadius: "50%" },
          display: {
            xs: "none",
            md: "flex",
          },
        }}
      >
        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          startDecorator={<KeyboardArrowLeftIcon />}
          onClick={handlePreviousPage}
          disabled={currentPage === 0}
        >
          Previous
        </Button>

        <Box sx={{ flex: 1 }} />
        {getPageNumbers().map((page) => (
          <IconButton
            key={typeof page === "number" ? page : "ellipsis"}
            size="sm"
            variant={currentPage + 1 === page ? "outlined" : "plain"}
            color="neutral"
            onClick={() => {
              if (typeof page === "number") setCurrentPage(page - 1);
            }}
            disabled={typeof page !== "number"}
          >
            {typeof page === "number" ? page : "..."}
          </IconButton>
        ))}
        <Box sx={{ flex: 1 }} />

        <Button
          size="sm"
          variant="outlined"
          color="neutral"
          endDecorator={<KeyboardArrowRightIcon />}
          onClick={handleNextPage}
          disabled={indexOfLastRow >= filteredRows.length}
        >
          Next
        </Button>
      </Box>
    </React.Fragment>
  );
}
